// src/pages/Admin/Adherents/AttendanceList.jsx
import React, { useEffect, useMemo, useState } from "react";
import { getAttendanceList } from "../../../services/attendanceServices";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";

export default function AttendanceList({ adherentId }) {
  const [attendances, setAttendances] = useState([]);
  const [loading, setLoading] = useState(true);

  // Charger les présences de l’adhérent
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const data = await getAttendanceList(adherentId);
        if (!mounted) return;
        setAttendances(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Erreur lors de la récupération des présences:", e);
        if (!mounted) return;
        setAttendances([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [adherentId]);

  // Groupement par mois (clé AAAA-MM) + étiquette localisée FR
  const months = useMemo(() => {
    const map = new Map();
    for (const a of attendances) {
      const d = new Date(a?.date_presence);
      if (Number.isNaN(d.getTime())) continue;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const arr = map.get(key) || [];
      arr.push(a);
      map.set(key, arr);
    }

    // Tri mois du plus récent au plus ancien
    const sorted = [...map.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));

    return sorted.map(([key, items]) => {
      const [y, m] = key.split("-");
      const labelDate = new Date(Number(y), Number(m) - 1, 1);
      let label = labelDate.toLocaleDateString("fr-FR", {
        month: "long",
        year: "numeric",
      });
      label = label.charAt(0).toUpperCase() + label.slice(1); // Majuscule
      // Tri jours dans le mois
      items.sort(
        (i, j) =>
          new Date(i.date_presence).getTime() - new Date(j.date_presence).getTime()
      );
      return { key, label, items };
    });
  }, [attendances]);

  return (
    <section className="rounded-2xl overflow-hidden ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/60 backdrop-blur">
      {/* En-tête harmonisé */}
      <div className="px-5 pt-5 sm:px-6">
        <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100">
          Présences & absences
        </h3>
        <div className="mt-2 h-1 w-20 rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500" />
      </div>

      {/* Contenu */}
      <div className="px-5 pb-6 sm:px-6 sm:pb-6 mt-4">
        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="animate-pulse rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-5 bg-white/60 dark:bg-slate-900/40 h-24"
              />
            ))}
          </div>
        ) : months.length === 0 ? (
          <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-5 bg-slate-50 dark:bg-slate-900/40 text-sm text-slate-700 dark:text-slate-300">
            Aucune donnée de présence disponible.
          </div>
        ) : (
          <>
            {/* Légende */}
            <div className="mb-4 flex flex-wrap items-center gap-3 text-xs">
              <span className="inline-flex items-center gap-1 text-slate-600 dark:text-slate-300">
                <FaCheckCircle className="text-emerald-500" /> Présent
              </span>
              <span className="inline-flex items-center gap-1 text-slate-600 dark:text-slate-300">
                <FaTimesCircle className="text-rose-500" /> Absent
              </span>
            </div>

            {/* Grille par mois */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {months.map(({ key, label, items }) => {
                const present = items.filter((x) => !!x.presence).length;
                const absent = items.length - present;
                return (
                  <article
                    key={key}
                    className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-slate-50 dark:bg-slate-900/40"
                  >
                    {/* Titre mois + compteurs */}
                    <div className="flex items-start justify-between gap-3">
                      <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {label}
                      </h4>
                      <div className="flex items-center gap-1">
                        <span className="inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold bg-emerald-100 text-emerald-900 ring-1 ring-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:ring-emerald-800/50">
                          {present}
                        </span>
                        <span className="inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold bg-rose-100 text-rose-900 ring-1 ring-rose-200 dark:bg-rose-900/40 dark:text-rose-200 dark:ring-rose-800/50">
                          {absent}
                        </span>
                      </div>
                    </div>

                    {/* Liste jours */}
                    <ul className="mt-3 space-y-1.5 text-sm">
                      {items.map((a) => {
                        const d = a?.date_presence
                          ? new Date(a.date_presence).toLocaleDateString("fr-FR")
                          : "—";
                        return (
                          <li
                            key={a.id}
                            className="flex items-center justify-between rounded-lg px-2 py-1.5 bg-white dark:bg-slate-900 ring-1 ring-slate-200 dark:ring-slate-800"
                          >
                            <span className="text-slate-700 dark:text-slate-300">
                              {d}
                            </span>
                            {a?.presence ? (
                              <FaCheckCircle className="text-emerald-500" />
                            ) : (
                              <FaTimesCircle className="text-rose-500" />
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </article>
                );
              })}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
